import { useEffect, useState, useRef } from 'react';
import { debounce } from 'lodash';
import { Grid, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { BlankMenuItem } from '../../util/SharedStyles';
import { Viewer } from '../../util/Constants';
import FilterBar from './FilterBar';

interface ExternalConnectionsFilterBarProps {
    id?: string;
    loading: boolean;
    visible: boolean;
    viewer: Viewer | undefined;
    onClose: () => void;
    onFilterChanged: (
        filterIsForClient?: boolean | undefined,
        filterIsForPartner?: boolean | undefined
    ) => void;
}

const ExternalConnectionsFilterBar: React.FC<ExternalConnectionsFilterBarProps> = props => {
    const { id, loading = false, visible = false, viewer, onFilterChanged } = props;
    const [filterAccessibleFor, setFilterAccessibleFor] = useState<string>('');
     
    const fieldsDisabled = visible && loading;

    // Debounced function would be recreated on every render, so to prevent this,
    // Wrap in React useRef to store the debounced function across renders
    const debouncedSearch = useRef(
        debounce((filterValue: string) => {
            // Call search with the filter value directly, not relying on state
            const { filterIsForClient, filterIsForPartner } = getFilterValuesFromString(filterValue);
            onFilterChanged(filterIsForClient, filterIsForPartner);
        }, 300)
    ).current;

    useEffect(() => {
        // need to send out an initial fetch 
        onSearchClick();
    }, []);

    const onSearchClick = () => {
        const { filterIsForClient, filterIsForPartner } = getFilterValues();
        onFilterChanged(filterIsForClient, filterIsForPartner);
    };

    const onCloseClick = () => {
        // remove any filters
        clearFilters();

        // call parent to hide bar
        props.onClose();

        // trigger a new search with filters undefined since state of filter values might not be updated yet
        onFilterChanged(undefined, undefined);
    };

    const clearFilters = () => {
        setFilterAccessibleFor('');
    };

    const getFilterValues = () => {
        let filterIsForClient: boolean | undefined = undefined;
        let filterIsForPartner: boolean | undefined = undefined;

        if (filterAccessibleFor === 'client') {
            filterIsForClient = true;
        } else if (filterAccessibleFor === 'partner') {
            filterIsForPartner = true;
        }

        return { filterIsForClient, filterIsForPartner };
    };

    const getFilterValuesFromString = (filterValue: string) => {
        let filterIsForClient: boolean | undefined = undefined;
        let filterIsForPartner: boolean | undefined = undefined;

        if (filterValue === 'client') {
            filterIsForClient = true;
        } else if (filterValue === 'partner') {
            filterIsForPartner = true;
        }

        return { filterIsForClient, filterIsForPartner };
    };

    const getAccessibleForFilterList = () => {
        const options = [
            { value: 'client', label: 'Client' },
            { value: 'partner', label: 'Partner' }
        ];

        let items = [];
        const blankItem = <BlankMenuItem key="0" value=""></BlankMenuItem>;
        const mappedItems = options.map((option) => (
            <MenuItem
                key={option.value}
                value={option.value}
                disabled={fieldsDisabled}
            >
                {option.label}
            </MenuItem>
        ));
        
        items.push(blankItem);
        items.push(...mappedItems);
        return items;
    };
    
    const onAccessibleForFilterChange = (event: SelectChangeEvent<string>) => {
        const {
            target: { value },
        } = event;
        
        let currentValue = value?.length > 0 ? value : '';
        
        // Update state immediately for UI responsiveness
        setFilterAccessibleFor(currentValue);
        
        // Call debounced search with the actual value
        debouncedSearch(currentValue);
    };

    const accessibleForMenuProps = {
        PaperProps: {
            style: {
                maxHeight: '300px',
            },
        },
    };
   
    return (
        <FilterBar id={id} visible={visible} onClose={onCloseClick} onSearch={onSearchClick}>
            <Grid item xs={2}>
                <FormControl variant="standard" fullWidth>
                    <InputLabel id="external-connection-filter-accessible-for-label">Accessible For</InputLabel>
                    <Select
                        labelId="external-connection-filter-accessible-for-label"
                        aria-labelledby="external-connection-filter-accessible-for-label"
                        value={filterAccessibleFor}
                        onChange={onAccessibleForFilterChange}
                        disabled={fieldsDisabled}
                        MenuProps={accessibleForMenuProps}
                        data-cy="external-connection-filter-accessible-for"
                    >
                        {getAccessibleForFilterList()}
                    </Select>
                </FormControl>
            </Grid>
        </FilterBar>
    );
};

export default ExternalConnectionsFilterBar;
